import Keycloak from 'keycloak-js';

const url = import.meta.env.VITE_KEYCLOAK_URL;
const realm = import.meta.env.VITE_KEYCLOAK_REALM;
const clientId = import.meta.env.VITE_KEYCLOAK_CLIENT;

export const ssoEnabled = !!url;

const redirectUri = () => window.location.origin + '/login';
let kc = null;
let initPromise = null;

// keycloak-js đọc code/state trên URL (nếu có) và đổi lấy token với PKCE S256.
function init() {
    if (!initPromise) {
        kc = new Keycloak({ url, realm, clientId });
        initPromise = kc.init({ pkceMethod: 'S256', checkLoginIframe: false, responseMode: 'query', redirectUri: redirectUri() });
        // Init lỗi thì cho phép thử lại, không giữ promise bị reject.
        initPromise.catch(() => { initPromise = null; });
    }
    return initPromise;
}

export async function ssoLogin() {
    await init();
    await kc.login({ redirectUri: redirectUri() });
}

export async function ssoHandleCallback() {
    if (!ssoEnabled) return null;
    const p = new URLSearchParams(window.location.search);
    if (p.has('error')) {
        // Người dùng huỷ / Keycloak từ chối (vd. access_denied): bỏ query và báo lỗi.
        window.history.replaceState({}, document.title, window.location.pathname);
        throw new Error('SSO callback error: ' + p.get('error'));
    }
    if (!p.has('code') || !p.has('state')) return null;
    try {
        // Ném lỗi (init lỗi hoặc không xác thực được) để Login.vue báo cho người dùng.
        if (!(await init())) throw new Error('SSO callback not authenticated');
        return { accessToken: kc.token, idToken: kc.idToken };
    } finally {
        // Xoá code/state khỏi thanh địa chỉ ở mọi nhánh để không dùng lại khi reload.
        window.history.replaceState({}, document.title, window.location.pathname);
    }
}

export function ssoLogoutUrl(idToken) {
    const q = new URLSearchParams({ client_id: clientId, post_logout_redirect_uri: redirectUri(), id_token_hint: idToken });
    return `${url}/realms/${realm}/protocol/openid-connect/logout?${q}`;
}

// Xác thực lại trước khi chi tự động (spec 2026-11-05 mục 4): popup Keycloak prompt=login + max_age=0 (bắt nhập lại mật khẩu
// + OTP dù còn phiên), PKCE S256; /sso-reauth.html gửi code về đây bằng postMessage. Trả access_token để gửi kèm
// header X-Reauth-Token. Backend kiểm auth_time, sub = người đang đăng nhập, jti dùng một lần.
export const payoutReauthEnabled = import.meta.env.VITE_PAYOUT_REAUTH === 'true';

const REAUTH_CANCELLED = 'Chưa xác thực lại, chưa chi';
const b64url = (bytes) => btoa(String.fromCharCode(...new Uint8Array(bytes))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
const randomString = () => b64url(crypto.getRandomValues(new Uint8Array(32)));

export function ssoReauth(loginHint) {
    // Mở popup đồng bộ, trước mọi await, để vẫn nằm trong cú click của thu ngân (trình duyệt chặn popup mở muộn).
    const popup = window.open('about:blank', 'sso-reauth', 'width=480,height=680');
    if (!popup) return Promise.reject(new Error(`Trình duyệt chặn cửa sổ xác thực. Cho phép popup cho ${window.location.host} rồi bấm lại`));
    const redirectUri = window.location.origin + '/sso-reauth.html';
    const base = `${url}/realms/${realm}/protocol/openid-connect`;
    return new Promise((resolve, reject) => {
        let state, verifier, done = false;
        const finish = (fn, v) => {
            if (done) return;
            done = true;
            window.removeEventListener('message', onMessage);
            clearInterval(timer);
            clearTimeout(timeout);
            if (!popup.closed) popup.close();
            fn(v);
        };
        const fail = (msg) => finish(reject, new Error(msg));
        const onMessage = async (e) => {
            if (e.origin !== window.location.origin || e.source !== popup || e.data?.type !== 'sso-reauth') return;
            const p = new URLSearchParams(e.data.search || '');
            if (p.get('error') || p.get('state') !== state || !p.get('code')) return fail(REAUTH_CANCELLED);
            try {
                const res = await fetch(`${base}/token`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                    body: new URLSearchParams({ grant_type: 'authorization_code', code: p.get('code'), redirect_uri: redirectUri, client_id: clientId, code_verifier: verifier }),
                });
                const token = res.ok ? (await res.json()).access_token : null;
                token ? finish(resolve, token) : fail(REAUTH_CANCELLED);
            } catch {
                fail(REAUTH_CANCELLED);
            }
        };
        window.addEventListener('message', onMessage);
        const timer = setInterval(() => { if (popup.closed) fail(REAUTH_CANCELLED); }, 500);
        const timeout = setTimeout(() => fail(REAUTH_CANCELLED), 5 * 60 * 1000);
        (async () => {
            state = randomString();
            verifier = randomString();
            const challenge = b64url(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier)));
            const q = new URLSearchParams({
                client_id: clientId, redirect_uri: redirectUri, response_type: 'code', scope: 'openid', state, nonce: randomString(),
                code_challenge: challenge, code_challenge_method: 'S256', prompt: 'login', max_age: '0',
            });
            if (loginHint) q.set('login_hint', loginHint);
            popup.location.href = `${base}/auth?${q}`;
        })().catch(() => fail(REAUTH_CANCELLED));
    });
}
